import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import seniorApi, { IFunctionPermissionItem, IFunctionPermissions } from '~/apis/senior';
import { SettingsCard, SwitchRow } from '~/components/module/setup/controls';
import { getErrorMessage } from '~/utils/helper/error';
import { canViewModule } from '~/utils/helper/permission';
import { useAppSelector } from '~/utils/redux/hooks';
import { selectPermission, selectUser } from '~/thunks/auth/authSlice';
import { setupRouteAbsolute } from '~/utils/routes/setup';

// Same grouping as the app (and the "Functions of SmartSeniors" default table).
const FUNCTION_GROUPS: { key: string; functionKeys: string[] }[] = [
  { key: 'mainActions', functionKeys: ['GO_HOME', 'SOS', 'FLASHLIGHT'] },
  {
    key: 'callMessage',
    functionKeys: [
      'CALL_VIDEO',
      'CALL_VOICE',
      'MESSAGE_VIDEO',
      'MESSAGE_VOICE',
      'GROUP_CALL_VIDEO',
      'GROUP_CALL_VOICE',
      'MISSED_CALLS_BUTTON',
      'UNREAD_MESSAGES_BUTTON',
    ],
  },
  { key: 'tabs', functionKeys: ['ACTIVITIES_TAB', 'MEMORIES_TAB', 'MEDICAL_TAB'] },
];

const FUNCTION_LABEL_KEYS: Record<string, string> = {
  GO_HOME: 'goHome',
  SOS: 'sos',
  FLASHLIGHT: 'flashlight',
  CALL_VIDEO: 'callVideo',
  CALL_VOICE: 'callVoice',
  MESSAGE_VIDEO: 'messageVideo',
  MESSAGE_VOICE: 'messageVoice',
  GROUP_CALL_VIDEO: 'groupCallVideo',
  GROUP_CALL_VOICE: 'groupCallVoice',
  MISSED_CALLS_BUTTON: 'missedCallsButton',
  UNREAD_MESSAGES_BUTTON: 'unreadMessagesButton',
  ACTIVITIES_TAB: 'activitiesTab',
  MEMORIES_TAB: 'memoriesTab',
  MEDICAL_TAB: 'medicalTab',
};

const FunctionPermissions = () => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const permission = useAppSelector(selectPermission);
  const canUpdate = canViewModule(user, permission, 'seniorPhoneSetup');

  const [saved, setSaved] = useState<IFunctionPermissions>();
  const [useDefault, setUseDefault] = useState(true);
  const [items, setItems] = useState<IFunctionPermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await seniorApi.getFunctionPermissions()).data.data;
      setSaved(data);
      setUseDefault(data.useDefaultSettings);
      setItems(data.permissions);
    } catch {
      setFeedback({ type: 'error', message: t('pages.setup.loadFailed') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty =
    !!saved &&
    (useDefault !== saved.useDefaultSettings ||
      items.some((item) => item.isEnabled !== saved.permissions.find((p) => p.functionKey === item.functionKey)?.isEnabled));

  const find = (functionKey: string) => items.find((item) => item.functionKey === functionKey);

  const handleChange = (functionKey: string, isEnabled: boolean) => {
    setFeedback(undefined);
    setItems((prev) => prev.map((item) => (item.functionKey === functionKey ? { ...item, isEnabled } : item)));
  };

  const handleSync = async () => {
    setSyncing(true);
    setFeedback(undefined);
    try {
      await seniorApi.updateFunctionPermissions({
        useDefaultSettings: useDefault,
        // the response-only isAvailableForLevel isn't accepted by the backend's schema
        overrides: useDefault ? undefined : items.map(({ functionKey, isEnabled }) => ({ functionKey, isEnabled })),
      });
      await load();
      setFeedback({ type: 'success', message: t('pages.setup.syncSuccess') });
    } catch (error) {
      const reason = getErrorMessage(error);
      setFeedback({
        type: 'error',
        message: reason ? `${t('pages.setup.syncFailed')} ${reason}` : t('pages.setup.syncFailed'),
      });
    } finally {
      setSyncing(false);
    }
  };

  const fp = (key: string) => t(`pages.setup.functionPermissions.${key}`);

  return (
    <div className='flex w-full flex-col gap-4'>
      <Link to={setupRouteAbsolute.config} className='w-fit text-sm text-muted-foreground underline hover:text-foreground'>
        &larr; {t('nav.setup.config')}
      </Link>

      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-xl font-semibold text-foreground'>{fp('title')}</h1>
          <p className='text-sm text-muted-foreground'>{fp('subtitle')}</p>
        </div>
        {canUpdate && (
          <button
            type='button'
            onClick={handleSync}
            disabled={!isDirty || syncing}
            className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {syncing ? t('pages.setup.syncing') : t('pages.setup.sync')}
          </button>
        )}
      </div>

      {/* the header spans the page; the content below stays a readable width */}
      <div className='mx-auto flex w-full max-w-3xl flex-col gap-4'>
        {feedback && (
          <p className={`text-sm ${feedback.type === 'success' ? 'text-foreground' : 'text-destructive'}`} role='status'>
            {feedback.message}
          </p>
        )}
        {canUpdate && isDirty && <p className='text-xs text-muted-foreground'>{t('pages.setup.unsynced')}</p>}
        {!canUpdate && <p className='text-xs text-muted-foreground'>{t('pages.setup.readOnly')}</p>}

        {loading && items.length === 0 && <p className='text-sm text-muted-foreground'>{t('common.loading')}</p>}

        {saved && (
          <>
            <SettingsCard>
              <SwitchRow label={fp('defaultSetting')} checked={useDefault} disabled={!canUpdate} onChange={setUseDefault} />
              <p className='text-xs text-muted-foreground'>{fp('defaultSettingHint')}</p>
            </SettingsCard>

            {FUNCTION_GROUPS.map((group) => (
              <SettingsCard key={group.key}>
                <h2 className='text-sm font-semibold text-foreground'>{fp(`groups.${group.key}`)}</h2>
                <ul className='flex flex-col divide-y divide-border'>
                  {group.functionKeys.map((functionKey) => {
                    const item = find(functionKey);
                    const available = item?.isAvailableForLevel ?? true;
                    return (
                      <li key={functionKey} className='flex items-center justify-between gap-3 py-2 text-sm'>
                        <div className='min-w-0'>
                          <p className={available ? 'text-foreground' : 'text-muted-foreground'}>
                            {fp(`functions.${FUNCTION_LABEL_KEYS[functionKey]}`)}
                          </p>
                          {!available && (
                            <p className='text-xs text-muted-foreground'>
                              {t('pages.setup.functionPermissions.notAvailable', { level: saved.cognitiveDeclineLevel })}
                            </p>
                          )}
                        </div>
                        <input
                          type='checkbox'
                          aria-label={fp(`functions.${FUNCTION_LABEL_KEYS[functionKey]}`)}
                          checked={item?.isEnabled ?? false}
                          // at a level that forbids the function, or while following the default table, it is locked
                          disabled={!canUpdate || !available || useDefault}
                          onChange={(e) => handleChange(functionKey, e.target.checked)}
                        />
                      </li>
                    );
                  })}
                </ul>
              </SettingsCard>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default FunctionPermissions;
